import React, { useEffect, useState } from "react";

// Data
import { functionsList, hierarchiesList } from "constants/constants";

// Hooks
import { useAuth } from "utils/hooks";
import { useAppDispatch } from "app/hooks";
import useRedirectOnNoAdminPermission from "utils/hooks/useRedirectOnNoAdminPermission";

// Converters
import { convertPaceBehaviouralComparison } from "./adapters/converter.chart";

// Types
import {
    TPeopleData,
    TTeamsData,
    TFunction,
    THierarchy,
    IMultiFilters,
    ISelectedMultiFilters,
    TTeam,
    TPerson,
    TPersonReport,
} from "./BehaviouralComparison.types";
import { LD12Chart, SerieLabel } from "store/slice/charts";
import {
    BCIndividualsRefResponse,
    BCTeamsRefResponse,
    BehaviouralComparisonResponse,
    getBehaviouralComparisonData,
    getMyHubAssessmentChart,
    getPaceDashboardIndividualsRef,
    getPaceDashboardTeamsRef,
} from "services/pace/dashboard.service";
import { toggleLoader } from "store/slice/UI";
import { differenceBy, groupBy, isEmpty } from "lodash";
import { convertTeamsRef } from "./adapters/converter.teamsRef";
import { convertIndividualsRef } from "./adapters/converter.individualsRef";
import { urlPaths } from "enums/urlPaths";
import { COLORS } from "constants/chartCommons";
import { hubAverageColor } from "components/Charts/BehaviouralBreakdown/BehaviouralBreakdown.functions";
import { mergeIndividualIntoLD12Series, mergeLD12Series, removeFromLD12Series } from "utils";
import { TPaceAssessmentReportStatus } from "store/slice/Pace";
import { getAssessmentStatusService, TPaceAssessmentStatusServiceResponse } from "services/pace/assessment.service";
import { FulfilledPromiseResult } from "types/global";
import { convertIndividualsData } from "./adapters/converter.chart.individuals";
import { RequestInfo } from "store/slice/store.types";
import { format } from "date-fns";

export type TList = "individuals" | "teams";

export const mockedSavedFilters = [
    { id: 0, name: "Finance Set" },
    { id: 1, name: "CFO Set" },
    { id: 2, name: "ProductTeam" },
];

const defaultMetaParams = {
    page_size: 10,
    page_no: 1,
};

const chartRequestDefaultParams = {
    individuals: { user_ids: [] },
    averages: [],
};

const useBehaviouralComparison = () => {
    const dispatch = useAppDispatch();
    const { loggedInUserData } = useAuth();
    const orgId = loggedInUserData.selectedOrg?.id;

    useRedirectOnNoAdminPermission("SAAS_PACE_DASHBOARD_BEHAVIOURAL_COMPARISON", urlPaths.NotFound);

    const [fetchingReports, setFetchingReports] = useState<RequestInfo>("pristine");
    const [fetchingChartData, setFetchingChartData] = useState<RequestInfo>("pristine");

    const [chartData, setChartData] = useState<LD12Chart>({} as LD12Chart);
    const [showAverage, setShowAverage] = useState<boolean>(true);
    const [openTeamsModal, setOpenTeamsModal] = useState<boolean>(false);
    const [openPreviousPaceModal, setOpenPreviousPaceModal] = useState<boolean>(false);
    const [openBenchmarkPopover, setOpenBenchmarkPopover] = useState<boolean>(false);

    // Saved Filters
    const savedFilters = mockedSavedFilters;

    // Filters
    const [people, setPeople] = useState<TPeopleData>({ meta: defaultMetaParams, status: "pristine" } as TPeopleData);
    const [teams, setTeams] = useState<TTeamsData>({ meta: defaultMetaParams, status: "pristine" } as TTeamsData);
    const [functions, setFunctions] = useState<TFunction[]>(functionsList);
    const [hierarchies, setHierarchies] = useState<THierarchy[]>(hierarchiesList);

    const [cacheRequestingPeople, setCacheRequestingPeople] = useState<TPeopleData["data"]>([]);
    const [cacheRequestingTeam, setCacheRequestingTeam] = useState<TTeamsData["data"]>([]);

    const [multiFilterData, setMultiFilterData] = useState<IMultiFilters>({
        teams: [],
        functions: functionsList,
        hierarchies: hierarchiesList,
    });

    // Multi Select Field
    const [selectedMultiFilter, setSlelectedMultiFilter] = useState<ISelectedMultiFilters>({
        selectedTeams: [],
        selectedFunctions: [],
        selectedHierarchies: [],
    });

    const handleFilterDisabledData = (data: LD12Chart) => {
        const disabledItemsIndexes = data.series_labels.reduce((acc, val, i) => {
            return val.enabled ? acc : [...acc, i];
        }, [] as number[]);

        const newData = removeFromLD12Series(data, disabledItemsIndexes);

        return newData;
    };

    const handleToggleAverage = (event: React.ChangeEvent<HTMLInputElement>) => {
        const isChecked = event.target.checked;

        setShowAverage(event.target.checked);
        setChartData((prevData) => ({
            ...prevData,
            series_labels: prevData?.series_labels.map((serieLabel, i) => {
                const isHubAverage = serieLabel.label === "Hub Average";
                return {
                    ...serieLabel,
                    enabled: isHubAverage ? isChecked : serieLabel.enabled,
                };
            }),
        }));
    };

    const handleOpenBenchmarkPopover = () => {
        setOpenBenchmarkPopover(!openBenchmarkPopover);
    };

    const handleOnSave = () => {
        console.log("save clicked!");
    };

    const handleOnClear = () => {
        console.log("clear clicked!");
    };

    const handleOpenTeamsModal = (ev: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        ev.preventDefault();
        setOpenTeamsModal(true);
    };

    const handleCloseTeamsModal = async () => {
        setOpenTeamsModal(false);

        // When closing the modal request again the teams to make sure we have the lastest
        if (orgId) {
            const { data } = await getPaceDashboardTeamsRef({ ...defaultMetaParams, page_size: 50, orgId });
            handleSetResults<BCTeamsRefResponse>(data, "teams");
        }
    };

    const handleUpdateCurrent = (data: LD12Chart) => {
        return {
            ...data,
            series_labels: data.series_labels.map((serieLabel, i) => {
                const isPerson = serieLabel.entity_type === "person";
                const newName = isPerson ? `${serieLabel.label} [Current]` : serieLabel.label;
                return {
                    ...serieLabel,
                    label: newName,
                    entity_name: newName,
                };
            }),
        };
    };

    const handleUpdateAssessmentsData = (
        userId: number,
        selectedReports: TPersonReport[],
        assessmentsChartData: LD12Chart,
        chartData: LD12Chart
    ) => {
        let currentAssessmentUserIndex: number;
        let newChartData = chartData;
        let currentAssessmentId = 0;

        let newSeriesLabel = [] as SerieLabel[];
        let newSeries = {} as LD12Chart["series"];

        //
        // Update user's "Current" assessment data
        // ----------------------------------------------------------------
        newSeriesLabel = newChartData.series_labels.map((serie_label, serieLabelIndex) => {
            if (serie_label.id !== userId || serie_label.entity_parent) {
                return serie_label;
            }

            const currentAssessment = selectedReports[0];
            const labelInBrackets = currentAssessment.isCurrent ? "[Current]" : `[${currentAssessment.label}]`;
            const newName = `${currentAssessment.userName} ${labelInBrackets}`;
            currentAssessmentUserIndex = serieLabelIndex;
            currentAssessmentId = currentAssessment.user_assessment_id;

            return {
                ...serie_label,
                label: newName,
                entity_name: newName,
                entity_id: currentAssessment.user_assessment_id,
            };
        });

        const assessmentIndex = assessmentsChartData.series_labels.findIndex(
            (s) => s.entity_id === currentAssessmentId
        );
        const serieData = Object.entries(assessmentsChartData.series).flatMap((s) => s[1][assessmentIndex]);
        const updatedSeries = Object.entries(newChartData.series).map((serie, serieIndex) => {
            const data = serie[1].map((val, valIndex) =>
                valIndex === currentAssessmentUserIndex ? serieData[serieIndex] : val
            );
            return [serie[0], data];
        });
        newSeries = Object.fromEntries(updatedSeries);

        newChartData = {
            ...newChartData,
            series_labels: newSeriesLabel,
            series: newSeries,
        };

        //
        // Add checked Data
        // ----------------------------------------------------------------

        // Save the indexes of the assessments to add
        const chartDataAssessmentsIds = newChartData.series_labels.map((s) => s.entity_id);
        const selectedUserAssessmentsIds = selectedReports.map((a) => a.user_assessment_id);

        const chartDataToAddIndexes = selectedUserAssessmentsIds
            .filter((o) => !chartDataAssessmentsIds.includes(o))
            .map((id) => {
                return assessmentsChartData.series_labels.findIndex((s) => s.entity_id === id);
            });

        const updateSeriesLabels = assessmentsChartData.series_labels
            .filter((s) => s.id === userId)
            .slice(1)
            .filter((s) => !chartDataAssessmentsIds.includes(s.entity_id))
            .map((serieLabel, i) => {
                const parentItem = chartData.series_labels.find((s) => s.id === serieLabel.entity_parent);
                const serieColor = parentItem ? `${parentItem.color}50` : COLORS[i - 1];

                return {
                    ...serieLabel,
                    enabled: serieLabel.enabled,
                    color: serieColor,
                    isLegendExpanded: false,
                };
            });

        newSeriesLabel = [...newChartData.series_labels, ...updateSeriesLabels];

        const newDataSeries = Object.entries(assessmentsChartData.series).map((serie) => {
            const data = serie[1].filter((_, i) => chartDataToAddIndexes.includes(i));
            return [serie[0], data];
        });

        const newDataObject = Object.fromEntries(newDataSeries);
        newSeries = mergeLD12Series(newSeries, newDataObject);

        // Update charData before removing
        newChartData = {
            ...chartData,
            series_labels: newSeriesLabel,
            series: newSeries,
        };

        //
        // Remove unchecked Data
        // ----------------------------------------------------------------

        // Look for difference in actual data vs new data
        const chartDataPeople = newChartData.series_labels.filter((s) => s.entity_type === "person" && s.entity_parent);
        const seriesToRemove = differenceBy(chartDataPeople, assessmentsChartData.series_labels, "entity_id");

        const possibleRepeatedSeriesIndexes = newChartData.series_labels.reduce<number[]>((acc, s, i) => {
            if (currentAssessmentId === s.entity_id && s.entity_parent && s.id === userId) {
                return [...acc, i];
            }
            return acc;
        }, []);

        let indexestoRemove = seriesToRemove.map((serieLabel) =>
            newChartData.series_labels.findIndex((serie) => serie.entity_id === serieLabel.entity_id)
        );
        indexestoRemove = [...indexestoRemove, ...possibleRepeatedSeriesIndexes];

        newSeriesLabel = newChartData.series_labels.filter((_, i) => !indexestoRemove.includes(i));

        const finalSeries = Object.entries(newChartData.series).map((serie, serieIdx) => {
            const data = serie[1].filter((_, i) => !indexestoRemove.includes(i));
            return [serie[0], data];
        });
        newSeries = Object.fromEntries(finalSeries);

        // Update charData
        newChartData = {
            ...chartData,
            series_labels: newSeriesLabel,
            series: newSeries,
        };

        return newChartData;
    };

    const handleUpdateChartData = (data: LD12Chart) => {
        if (isEmpty(chartData)) {
            setChartData({
                ...data,
                series_labels: data.series_labels.map((serieLabel, i) => ({
                    ...serieLabel,
                    enabled: showAverage,
                    color: hubAverageColor,
                    isLegendExpanded: false,
                })),
            });
            return;
        }

        setChartData((prevData) => {
            // Merge Series Labels
            const newSeriesLabels = [...(prevData.series_labels || []), ...data.series_labels];
            const series_labels = newSeriesLabels.map((serieLabel, i) => {
                const isHubAverage = serieLabel.label === "Hub Average";
                const parentItem = newSeriesLabels.find((s) => s.id === serieLabel.entity_parent);
                const serieColor = parentItem ? `${parentItem.color}50` : COLORS[i - 1];

                return {
                    ...serieLabel,
                    enabled: isHubAverage ? showAverage : serieLabel.enabled,
                    color: isHubAverage ? hubAverageColor : serieColor,
                    isLegendExpanded: false,
                };
            });

            // Merge Series
            const series = mergeLD12Series(prevData.series, data.series);

            return {
                ...prevData,
                series_labels,
                series,
            };
        });

        setCacheRequestingPeople([]);
    };

    const handleFetchChart = async <T extends TPeopleData["selected"] | TTeamsData["selected"]>(
        selectedData: T,
        type: TList,
        avoidFetching = false
    ) => {
        if (!orgId || avoidFetching || !selectedData.length) return;

        let params = {};

        if (type === "individuals") {
            params = {
                individuals: {
                    id_suffix: "individuals",
                    user_ids: (selectedData as TPerson[]).filter((p) => p.checked).map((p) => p.id),
                },
                averages: [],
            };
        }

        if (type === "teams") {
            params = {
                individuals: {
                    id_suffix: "individuals",
                    user_ids: [],
                },
                averages: (selectedData as TTeam[])
                    .filter((t) => t.checked)
                    .map((t) => ({
                        id_suffix: t.name.replaceAll(" ", "_"),
                        teams: [t.name],
                    })),
            };
        }

        try {
            setFetchingChartData("fetching");
            const { data } = await getBehaviouralComparisonData(orgId, params);
            setFetchingChartData("done");
            return data;
        } catch (err) {
            console.error(err);
            setFetchingChartData("error");
        }
    };

    const handleSetReportData = (people: TPeopleData, reports: TPaceAssessmentReportStatus[]) => {
        const newPeopleData = {
            ...people,
            data: people.data.map((person) => {
                const currentUser = reports.find((r) => r?.user_id === person.id);
                const assessments = currentUser?.user_assessments;
                const completedAssessments = assessments?.filter((a) => a.meta.completed);
                const currentAssessmentId = assessments?.[0].user_assessment_id;

                if (!assessments?.length) {
                    return person;
                }

                return {
                    ...person,
                    reports: completedAssessments?.map((assessment, i) => {
                        const isCurrentAssessment = currentAssessmentId === assessment.user_assessment_id;
                        return {
                            ...assessment,
                            userName: person.name,
                            userId: person.id,
                            isCurrent: currentAssessmentId === assessment.user_assessment_id,
                            label: format(new Date(assessment.meta.completed), "dd LLL, yy"),
                            checked: !person.reports?.[i]?.hasOwnProperty("checked")
                                ? isCurrentAssessment
                                : person.reports?.[i].checked,
                        };
                    }),
                };
            }),
        };

        setPeople(newPeopleData);
        setCacheRequestingPeople([]);
    };

    const handleOpenPreviousPaceModal = async () => {
        setOpenPreviousPaceModal(true);

        const selectedPeople = people.data.filter((person) => person.checked);

        if (!selectedPeople.length) {
            return;
        }

        try {
            setFetchingReports("fetching");

            const reports = await handleFetchReports(selectedPeople);
            handleSetReportData(people, reports);

            setFetchingReports("done");
        } catch (err) {
            console.error(err);
            setFetchingReports("error");
        }
    };

    const handleClosePreviousPaceModal = async (shouldAddData = false) => {
        if (shouldAddData === false) {
            setOpenPreviousPaceModal(false);
            return;
        }

        // Get selected assessments
        const selectedAssessments = people.data
            .flatMap((p) => p.reports?.filter((r) => r.checked))
            .filter((r): r is TPersonReport => {
                return r !== undefined;
            });

        const selectedAssessmentsIds = selectedAssessments.map((r) => r?.user_assessment_id);

        if (selectedAssessmentsIds.length) {
            // Request the data for the missing assessments in chart
            const response = await handleFetchAssessmentsGraph(selectedAssessmentsIds);

            if (response) {
                const assessmentsGraph = Object.values(response).flatMap((e) => e.graphs);
                // Use chartData rest as a starter value
                const { series, series_labels, ...rest } = chartData;

                // Convert assessments into LD12 series and series_labels and merge it with chartData's rest
                const assessmentsChartData: LD12Chart = assessmentsGraph.reduce((acc, graph) => {
                    const userId = graph.data_series?.score_y?.[0]["user.id"];
                    const userData = convertIndividualsData(graph, "date", userId);
                    if (userData) {
                        return mergeIndividualIntoLD12Series(acc, userData);
                    } else {
                        return acc;
                    }
                }, rest as LD12Chart);

                // Transform the data per user
                // Create a loop to pass the list of assessments of the user and the current data
                // work with that and for the next user use the previous data and so on

                const assessmentsPerUser = Object.values(groupBy(selectedAssessments, "userId"));

                let newChartData = { ...chartData };
                assessmentsPerUser.forEach((assessmentList) => {
                    newChartData = handleUpdateAssessmentsData(
                        assessmentList[0].userId,
                        assessmentList,
                        assessmentsChartData,
                        newChartData
                    );
                });

                setChartData(newChartData);
            }
        }

        setOpenPreviousPaceModal(false);
    };

    const handleFetchAssessmentsGraph = async (assessmentsIds: number[]) => {
        try {
            dispatch(toggleLoader(true));
            const result = await getMyHubAssessmentChart(assessmentsIds, true);
            return Object.values(result.data).map((e) => {
                return {
                    ...e,
                    graphs: e.graphs.map((g: any) => {
                        return {
                            ...g,
                            data_series: {
                                ...g.data_series,
                                score_y: [
                                    {
                                        ...g.data_series.score_y[0],
                                        "user.id": e.user_id,
                                    },
                                ],
                            },
                        };
                    }),
                };
            });
        } catch (err) {
            console.error(err);
        } finally {
            dispatch(toggleLoader(false));
        }
    };

    const handleUpdateReports = (isChecked: boolean, personId?: number, assessmentId?: number) => {
        let newPeopleData = {} as TPeopleData;

        if (!personId && !assessmentId) {
            newPeopleData = {
                ...people,
                data: people.data.map((person) => {
                    return {
                        ...person,
                        reports: person.reports?.map((assessment, i) => {
                            // Keeps at least the 1st assessment selected for this user
                            const checked = i === 0 && !isChecked ? true : isChecked;
                            return {
                                ...assessment,
                                checked,
                            };
                        }),
                    };
                }),
            };
        } else {
            newPeopleData = {
                ...people,
                data: people.data.map((person) => {
                    if (personId === person.id) {
                        return {
                            ...person,
                            reports: person.reports?.map((assessment) => {
                                if (assessmentId === assessment.user_assessment_id) {
                                    return {
                                        ...assessment,
                                        checked: isChecked,
                                    };
                                }
                                return assessment;
                            }),
                        };
                    }
                    return person;
                }),
            };
        }

        setPeople(newPeopleData);
    };

    const handleFetchReports = async (peopleWithData: TPerson[]) => {
        // Fetch Report Status for every user
        const peopleReportsPromise = peopleWithData.map(async (person) => {
            // Return the promise to fetch the report status
            return getAssessmentStatusService(person.id);
        });

        const reportResponse = await Promise.allSettled(peopleReportsPromise);

        // Get the data only from the fullfilled ones
        return reportResponse
            .filter((r): r is FulfilledPromiseResult<TPaceAssessmentStatusServiceResponse> => {
                return r.status === "fulfilled";
            })
            .map((r) => {
                return r.value.data;
            });
    };

    const handleSelectPerson = async (data: TPeopleData, avoidFetching = false) => {
        const checkedPeople = data.data.filter((p) => p.checked);
        const selectedPeople = differenceBy(checkedPeople, cacheRequestingPeople, "id");
        const peopleWithoutData = differenceBy(selectedPeople, chartData.series_labels, "id");

        setPeople(data);
        setCacheRequestingPeople((prevData) => [...prevData, ...selectedPeople]);

        setChartData((prevData) => ({
            ...prevData,
            series_labels: prevData.series_labels.map((serieLabel) => {
                const foundInList = data.data.find((p) => p.id === serieLabel.id);
                return {
                    ...serieLabel,
                    enabled: foundInList ? foundInList.checked : serieLabel.enabled,
                };
            }),
        }));

        const personData = await handleFetchChart<TPeopleData["selected"]>(
            peopleWithoutData,
            "individuals",
            avoidFetching
        );

        if (!personData) return;

        const newPersonData = { graphs: [...personData.graphs.slice(0, 1), ...personData.graphs.slice(2)] };
        let convertedChartData = convertPaceBehaviouralComparison(newPersonData);
        convertedChartData = handleUpdateCurrent(convertedChartData);

        handleUpdateChartData(convertedChartData);

        const reportsData = newPersonData.graphs
            .filter((graph) => graph.id === "INDIVIDUALS.individuals")[0]
            .data_series?.score_y?.map((graphData) => {
                return {
                    count_active: 0,
                    count_completed: 1,
                    user_id: graphData["user.id"],
                    user_assessments: [
                        {
                            user_assessment_id: graphData["assessment.id"],
                            meta: {
                                is_active: false,
                                status: "FINISHED_COMPLETED",
                                started: "",
                                completed: graphData.assessment_finished_utc,
                            },
                            assessment: {
                                assessment_id: 0,
                                assessment_version: "v0.1",
                            },
                            progress: {
                                meta: {
                                    meter: {
                                        count_answers: 107,
                                        count_questions: 107,
                                        percentage: 100,
                                    },
                                },
                            },
                        },
                    ],
                };
            })
            .filter((e) => e !== undefined) as TPaceAssessmentReportStatus[];

        handleSetReportData(data, reportsData);
    };

    const handleSelectTeam = async (data: TTeamsData, avoidFetching = false) => {
        const checkedTeam = data.data.filter((t) => t.checked);
        const selectedTeam = differenceBy(checkedTeam, cacheRequestingTeam, "label");
        const missingTeam = differenceBy(selectedTeam, chartData.series_labels, "label");

        setCacheRequestingTeam((prevData) => [...prevData, ...selectedTeam]);

        setTeams(data);
        setChartData((prevData) => ({
            ...prevData,
            series_labels: prevData.series_labels.map((serieLabel) => {
                const foundInList = data.data.find((t) => t.label === serieLabel.label);
                return {
                    ...serieLabel,
                    enabled: foundInList ? foundInList.checked : serieLabel.enabled,
                };
            }),
        }));

        const teamData = await handleFetchChart<TTeamsData["selected"]>(missingTeam, "teams", avoidFetching);

        if (!teamData) return;

        const newteamData = { graphs: [...teamData.graphs.slice(0, 1), ...teamData.graphs.slice(2)] };
        const convertedChartData = convertPaceBehaviouralComparison(newteamData);
        setCacheRequestingTeam([]);
        handleUpdateChartData(convertedChartData);
    };

    const handleSetResults = <T extends BCIndividualsRefResponse | BCTeamsRefResponse>(data: T, type: TList) => {
        const { results, meta } = data;

        if (type === "individuals") {
            const convertedIndividualsRef = convertIndividualsRef(
                results as BCIndividualsRefResponse["results"],
                people.selected
            );

            setPeople({
                ...people,
                status: "done",
                data: convertedIndividualsRef,
                meta,
            });
        }

        if (type === "teams") {
            const convertedTeamsRef = convertTeamsRef(results as BCTeamsRefResponse["results"], teams.selected);

            setTeams({
                ...teams,
                status: "done",
                data: convertedTeamsRef,
                meta,
            });

            setMultiFilterData((prevVal) => ({
                ...prevVal,
                teams: convertedTeamsRef,
            }));
        }
    };

    const handlePageChange = async (ev: React.ChangeEvent<unknown>, newPage: number, type: TList) => {
        if (!orgId) return;

        if (type === "individuals") {
            setPeople({ ...people, status: "fetching" });
            const { data } = await getPaceDashboardIndividualsRef({
                ...defaultMetaParams,
                filter_pace_only: true,
                page_no: newPage,
                orgId,
            });
            handleSetResults<BCIndividualsRefResponse>(data, "individuals");
        }

        if (type === "teams") {
            const { data } = await getPaceDashboardTeamsRef({ ...defaultMetaParams, page_no: newPage, orgId });
            handleSetResults<BCTeamsRefResponse>(data, "teams");
        }
    };

    useEffect(() => {
        const selectedItems = {
            selectedTeams: multiFilterData.teams.filter((team) => team.checked).map((team) => team.name),
            selectedFunctions: multiFilterData.functions.filter((func) => func.checked).map((func) => func.name),
            selectedHierarchies: multiFilterData.hierarchies.filter((hier) => hier.checked).map((hier) => hier.name),
        };
        setSlelectedMultiFilter(selectedItems);
    }, [multiFilterData]);

    // Init
    useEffect(() => {
        // Fetch chart data
        const initialFetch = async () => {
            if (!orgId) return;

            const promises = [];

            if (!people.data?.length) {
                setPeople({ ...people, status: "fetching" });
                promises.push(getPaceDashboardIndividualsRef({ ...defaultMetaParams, filter_pace_only: true, orgId }));
            }

            if (!teams.data?.length) {
                setPeople({ ...people, status: "fetching" });
                promises.push(getPaceDashboardTeamsRef({ ...defaultMetaParams, page_size: 50, orgId }));
            }

            if (isEmpty(chartData)) {
                promises.push(getBehaviouralComparisonData(orgId, chartRequestDefaultParams));
            }

            if (promises.length) {
                try {
                    dispatch(toggleLoader(true));

                    const response = await Promise.allSettled(promises);

                    for (const result of response) {
                        if (result.status === "rejected") {
                            return {
                                error: true,
                                reason: result.reason,
                            };
                        }

                        if (result.status === "fulfilled") {
                            if (result.value.config.url?.includes("individuals")) {
                                const data = result.value.data as BCIndividualsRefResponse;
                                handleSetResults<BCIndividualsRefResponse>(data, "individuals");
                            }

                            if (result.value.config.url?.includes("teams")) {
                                const data = result.value.data as BCTeamsRefResponse;
                                handleSetResults<BCTeamsRefResponse>(data, "teams");
                            }

                            if (result.value.config.url?.includes("behavioural_comparison")) {
                                const data = result.value.data as BehaviouralComparisonResponse;
                                let convertedChartData = convertPaceBehaviouralComparison(data);
                                convertedChartData = handleUpdateCurrent(convertedChartData);
                                handleUpdateChartData(convertedChartData);
                            }
                        }
                    }
                } catch (err) {
                    console.error(err);
                } finally {
                    dispatch(toggleLoader(false));
                }
            }
        };

        initialFetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        people,
        teams,
        functions,
        hierarchies,
        multiFilterData,
        chartData,
        savedFilters,
        showAverage,
        openTeamsModal,
        openPreviousPaceModal,
        openBenchmarkPopover,
        fetchingChartData,
        fetchingReports,
        setPeople,
        setTeams,
        setFunctions,
        setHierarchies,
        setMultiFilterData,
        handleToggleAverage,
        handleOnClear,
        handleOnSave,
        handleSelectPerson,
        handleSelectTeam,
        handlePageChange,
        handleOpenBenchmarkPopover,
        handleOpenTeamsModal,
        handleCloseTeamsModal,
        handleOpenPreviousPaceModal,
        handleClosePreviousPaceModal,
        handleSetResults,
        handleFilterDisabledData,
        handleUpdateReports,
    };
};

export default useBehaviouralComparison;
